import type { ClinicalDocumentData } from "@qendo/profile/files";
import { Stack } from "expo-router";
import { useState } from "react";
import { Button } from "~/components/Button";
import { Badge } from "~/components/feedback/Badge";
import { FilteredList } from "~/components/layout/FilteredList";
import { ListingItem } from "~/components/layout/ListingItem";
import { Page } from "~/components/layout/Page";
import { SectionHeader } from "~/components/layout/SectionHeader";
import { BodyText, Bold } from "~/components/layout/text";
import { Authorize } from "~/components/permission/Authorize";
import { Can } from "~/components/permission/Can";
import { ShareButton } from "~/components/ShareButton";
import { useGallery } from "~/providers/GalleryProvider";
import { filled } from "~/util";
import { plural, searchMatches } from "~/util/string";

export default function PainPlanIndex() {
    const { pain_plan, refreshDocuments } = useGallery();
    const [refreshing, setRefreshing] = useState(false);

    const refresh = async () => {
        if (refreshing) {
            return;
        }

        setRefreshing(true);

        await refreshDocuments().finally(() => setRefreshing(false));
    };

    const has_items = filled(pain_plan);

    return (
        <Authorize ability="view" section="documents">
            <Stack.Screen
                options={{
                    headerRight: () => <ShareButton title="Share documents" initial={["documents"]} />,
                }}
            />
            <Page spacer refreshWith={refresh} isRefreshing={refreshing} scrollable={!has_items}>
                <SectionHeader
                    title="Pain plan"
                    icon="prescribed-file"
                    button={() => (
                        <Can ability="update" section="documents">
                            <Button
                                title="Add prescribed pain plan"
                                variant="primary"
                                size="lg"
                                href="/health-profile/pain-plan/create"
                            />
                        </Can>
                    )}
                >
                    Plans to help you manage flares.
                </SectionHeader>
                <Badge
                    icon="info-circle"
                    title={
                        <BodyText size="md">
                            Prescribed pain plans also appear in the <Bold>Clinical documents</Bold> section of your
                            health profile.
                        </BodyText>
                    }
                    variant="info"
                    size="lg"
                />
                {filled(pain_plan) && (
                    <FilteredList<ClinicalDocumentData>
                        data={pain_plan}
                        searchWith={(item, term) => searchMatches(term, item.search)}
                        searchPlaceholder="Search by name"
                        refreshWith={refresh}
                        isRefreshing={refreshing}
                        renderItem={({ item }) => (
                            <ListingItem
                                title={item.name}
                                hideChevron
                                badge="Prescribed pain plan"
                                badgeVariant="info"
                                href={{ pathname: `/health-profile/pain-plan/[id]/show`, params: { id: item.id } }}
                                subtitle={filled(item.files) ? `${plural("File", item.files)} attached` : undefined}
                                last
                            />
                        )}
                    />
                )}
            </Page>
        </Authorize>
    );
}
